import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiFacebook, FiInstagram } = FiIcons;

// TikTok icon as inline SVG since it's not in react-icons/fi
const TikTokIcon = ({ size = 24, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.7a8.26 8.26 0 0 0 4.84 1.56V6.81a4.83 4.83 0 0 1-1.08-.12z" />
  </svg>
);

const containerVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      staggerChildren: 0.1,
      ease: [0.25, 0.1, 0.25, 1.0],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

const typingContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const typingLetter = {
  hidden: { opacity: 0, display: "none" },
  visible: {
    opacity: 1,
    display: "inline-block",
    transition: { duration: 0.01 },
  },
};

const SocialButton = ({ icon, href, isCustom }) => {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="w-12 h-12 p-2 border border-[#404040] rounded-xl relative overflow-hidden group hover:border-transparent flex items-center justify-center transition-colors"
      style={{
        background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.00) 100%)'
      }}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 duration-300 p-[1px] bg-[linear-gradient(99deg,#3B82F6_0%,#4F46E5_100%)] rounded-xl">
        <div className="w-full h-full bg-[#070709] rounded-[11px]"></div>
      </div>
      {isCustom ? (
        <span className="text-[#D6D6D6] group-hover:text-white relative z-10 transition-colors duration-300">
          {icon}
        </span>
      ) : (
        <SafeIcon icon={icon} size={24} className="stroke-[#D6D6D6] group-hover:stroke-white relative z-10 transition-colors duration-300" />
      )}
    </motion.a>
  );
};

const Footer = () => {
  const wordmark = "HOSTFIG";

  return (
    <motion.footer
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={containerVariants}
      className="bg-[#070709] w-full pt-[60px] md:pt-[80px] pb-10 px-6 md:px-0 xl:px-0 flex flex-col items-center overflow-hidden"
    >
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');`}
      </style>

      <svg width="0" height="0" className="absolute invisible">
        <linearGradient id="social-gradient" x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform="rotate(99)">
          <stop stopColor="#3B82F6" offset="0%" />
          <stop stopColor="#4F46E5" offset="100%" />
        </linearGradient>
      </svg>

      <motion.div
        variants={typingContainer}
        className="w-full max-w-[1248px] flex justify-center mb-10 md:mb-[80px] px-0 md:px-12 lg:px-6 xl:px-0 overflow-visible py-4"
      >
        <h1 className="text-center font-['Space_Grotesk'] font-bold tracking-tight md:tracking-[-6.75px] leading-[1.1] md:leading-[1] pointer-events-none select-none whitespace-nowrap pb-4 text-[15vw] sm:text-[16vw] md:text-[18vw] lg:text-[200px] xl:text-[240px] 2xl:text-[260px] bg-[linear-gradient(183deg,#1e3a8a_2.62%,#070709_105.88%)] bg-clip-text text-transparent">
          {wordmark.split("").map((char, index) => (
            <motion.span key={index} variants={typingLetter}>
              {char}
            </motion.span>
          ))}
        </h1>
      </motion.div>

      <div className="max-w-[1248px] w-full flex flex-col md:flex-row justify-between gap-y-16 mb-16 md:mb-20 px-6 md:px-0 lg:px-6 xl:px-0">
        <motion.div
          variants={itemVariants}
          className="w-full md:w-1/2 flex flex-col items-start gap-6 md:pl-12 lg:pl-0"
        >
          <img
            src="https://res.cloudinary.com/dfg8nrgvf/image/upload/v1783586850/No_Backgournd_just_logo_pl2cm4.png"
            alt="HostFig Logo"
            className="h-[80px] md:h-[100px] w-auto object-contain"
            referrerPolicy="no-referrer"
          />
          <p className="text-[#D6D6D6] text-[18px] font-['Space_Grotesk'] font-normal leading-[28px] max-w-[360px]">
            Your complete digital partner for building, hosting and scaling your business online.
          </p>
          <div className="flex gap-4">
            <SocialButton
              icon={<FiFacebook size={24} />}
              href="https://www.facebook.com/share/1EJqASbkjo/?mibextid=wwXIfr"
              isCustom={true}
            />
            <SocialButton
              icon={<FiInstagram size={24} />}
              href="https://www.instagram.com/hostfig?igsh=bmRzNGU1czd3dGxk&utm_source=qr"
              isCustom={true}
            />
            <SocialButton
              icon={<TikTokIcon size={24} />}
              href="https://www.tiktok.com/@hostfig?_r=1&_t=ZS-98hx9ArI2S5"
              isCustom={true}
            />
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="w-full md:w-1/2 flex items-start justify-start md:justify-end gap-x-16 lg:gap-x-24 md:pr-12 lg:pr-0"
        >
          <div className="flex flex-col gap-4">
            <h4 className="font-['Space_Grotesk'] text-[24px] font-medium leading-[30px] tracking-[-0.8px] bg-[linear-gradient(99deg,#3B82F6_0%,#4F46E5_100%)] bg-clip-text text-transparent mb-2">
              Services
            </h4>
            <div className="flex flex-col gap-4">
              <span className="text-[#D6D6D6] text-[18px] font-['Space_Grotesk'] font-normal leading-[28px]">Website Development</span>
              <span className="text-[#D6D6D6] text-[18px] font-['Space_Grotesk'] font-normal leading-[28px]">Hosting Services</span>
              <span className="text-[#D6D6D6] text-[18px] font-['Space_Grotesk'] font-normal leading-[28px]">Cloud Solutions</span>
              <span className="text-[#D6D6D6] text-[18px] font-['Space_Grotesk'] font-normal leading-[28px]">Digital Marketing</span>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <h4 className="font-['Space_Grotesk'] text-[24px] font-medium leading-[30px] tracking-[-0.8px] bg-[linear-gradient(99deg,#3B82F6_0%,#4F46E5_100%)] bg-clip-text text-transparent mb-2">
              Company
            </h4>
            <div className="flex flex-col gap-4">
              <Link to="/about" className="text-[#D6D6D6] text-[18px] font-['Space_Grotesk'] font-normal hover:text-blue-500 transition-colors">About Us</Link>
              <Link to="/portfolio" className="text-[#D6D6D6] text-[18px] font-['Space_Grotesk'] font-normal hover:text-blue-500 transition-colors">Our Work</Link>
              <Link to="/process" className="text-[#D6D6D6] text-[18px] font-['Space_Grotesk'] font-normal hover:text-blue-500 transition-colors">Process</Link>
              <Link to="/contact" className="text-[#D6D6D6] text-[18px] font-['Space_Grotesk'] font-normal hover:text-blue-500 transition-colors">Contact</Link>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div
        variants={itemVariants}
        className="max-w-[1248px] w-full pt-8 border-t border-[#404040]/30 flex flex-col md:flex-row flex-wrap justify-center items-center gap-y-6 text-center px-6 md:px-0 lg:px-6 xl:px-0"
      >
        <div className="font-['Space_Grotesk'] text-[#D6D6D6] text-[16px] leading-[24px] tracking-[-0.3px]">
          © {new Date().getFullYear()} HostFig. All Rights Reserved.
        </div>
      </motion.div>
    </motion.footer>
  );
};

export default Footer;